import { FC, useState } from "react";
import { Box, Grid, Paper, Typography, Button } from "@mui/material";
import __ from "src/utils/Translations";
import EmployeeAreaAvatar from "src/Pages/Company/OrganizationStructure/sharedComponents/EmployeeAreaAvatar";
import { Link, useParams } from "react-router-dom";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import InfoIcon from "src/Pages/Company/OrganizationStructure/assets/icons/InfoIcon";
import { useStructureStore } from "src/Pages/Company/OrganizationStructure/context/useStructureStore";
import useFetchArea from "src/Pages/Company/OrganizationStructure/api/useFetchArea";
import { Loader } from "src/Components/Loader/Loader";
import useFetchAreaHierarchy from "src/Pages/Company/OrganizationStructure/api/useFetchAreaHierarchy";
import EmployeeHierarchy from "src/Pages/Company/OrganizationStructure/Areas/Components/EmployeeHierarchy/index";
import EmployeeChip from "src/Pages/Company/OrganizationStructure/Areas/Components/EmployeeChip/index";
import EditArea from "src/Pages/Company/OrganizationStructure/Areas/PreviewArea/EditArea";
import BackButton from "src/Pages/Company/OrganizationStructure/sharedComponents/BackButton";
import { IAreaMembers } from "src/Pages/Company/OrganizationStructure/Areas/Area.models";
import { Avatar } from "@benefit-systems/hr-tools-ui";
import { organizationStructureAreaEdit } from "src/utils/RoleBasedSecurity/permissions";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import useFetchStructureData from "src/Pages/Company/OrganizationStructure/api/useFetchStructureData";
import useAnimationFrame from "src/Pages/Company/OrganizationStructure/utils/useAnimationFrame";
import useIsAhr from "src/utils/hooks/useIsAhr";

const PreviewArea: FC = () => {
  const { scrollTop } = useAnimationFrame();
  const companyId = useCompanyId();
  const structureId = useStructureStore((state) => state.structureId);
  const { areaId } = useParams<{ areaId: string }>();
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const hasPermissionsStructureAreaEdit = usePermission(
    organizationStructureAreaEdit
  );
  const isAhr = useIsAhr();

  const { data: structureData } = useFetchStructureData(companyId, structureId);

  const { data: areaData, isLoading: isLoadingAreaData } = useFetchArea({
    companyId,
    structureId,
    areaId,
    enabled: !!companyId && !!structureId && !!areaId,
    queryParams: {
      fields: "members",
    },
  });

  const isMember: boolean = areaData?.manager?.area?.id === areaId;

  const { data: hierarchyData, isLoading: isLoadingHierarchy } =
    useFetchAreaHierarchy(companyId, structureId, areaId);

  const exitEditModeHandler = () => {
    setIsEditMode(false);
    scrollTop();
  };

  if (isLoadingAreaData || !areaData || isLoadingHierarchy || !structureId) {
    return <Loader />;
  }

  const managerName = areaData.manager?.name;
  const managerArea = areaData.manager?.area?.label;

  return (
    <>
      {isEditMode ? (
        <Box sx={{ p: 0, mb: 1, width: "64px", height: "24.5px" }} />
      ) : (
        <BackButton sx={{ p: 0, mb: 1 }} />
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          marginTop: "24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "16px",
          }}
        >
          <Box>
            <Typography
              variant="h5"
              fontWeight="fontWeightBold"
              textTransform="uppercase"
            >
              {__("Obszar")}: {`${areaData.name}`}
            </Typography>
            <Typography sx={{ my: 1 }} variant="body2" color="#00000099">
              {`ID: ${areaId}`}
            </Typography>
            <Typography
              variant="body2"
              fontWeight="fontWeightMedium"
              textTransform="uppercase"
              sx={{ my: 1 }}
            >
              {__("Panel administracyjny")}
            </Typography>
          </Box>
          {hasPermissionsStructureAreaEdit && !isEditMode && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setIsEditMode(true)}
            >
              {__("Edytuj")}
            </Button>
          )}
        </Box>
        {isEditMode ? (
          <EditArea
            areaData={areaData}
            exitEditMode={exitEditModeHandler}
            structureId={structureId}
            structureData={structureData}
          />
        ) : (
          <>
            {areaData.name && (
              <>
                <Typography fontWeight="fontWeightMedium">
                  {__(`Nazwa`)}:
                </Typography>
                <Paper sx={{ p: 3 }}>
                  <Typography>{areaData.name}</Typography>
                </Paper>
              </>
            )}

            <Typography fontWeight="fontWeightMedium" sx={{ mt: 2 }}>
              {__(`Osoba zarządzająca`)}:
            </Typography>
            <Paper sx={{ p: 3 }}>
              {areaData.manager ? (
                <EmployeeAreaAvatar
                  key={areaData.manager.id}
                  name={areaData.manager.name}
                  fk={areaData.manager.fk}
                  initials={areaData.manager.initials}
                  position={areaData.manager.position.label}
                  area={areaData.manager.area.label}
                  avatar={areaData.manager.avatar}
                  presenceInformation={areaData.manager.presenceInformation}
                />
              ) : (
                <>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    <Avatar
                      size={10}
                      src={areaData.structure!.avatar ?? undefined}
                    />
                    <Typography
                      fontWeight="fontWeightMedium"
                      sx={{ fontSize: "14px", lineHeight: "12px" }}
                    >
                      {areaData.structure!.label}
                    </Typography>
                  </Box>
                </>
              )}

              {areaData?.manager && (
                <Typography>
                  {isMember ? (
                    <>
                      <InfoIcon />
                      {managerName} {__("jest obecnie członkiem tego obszaru")}
                    </>
                  ) : (
                    managerArea &&
                    managerArea.length > 0 && (
                      <>
                        <InfoIcon />
                        {managerName} {__("jest obecnie członkiem obszaru")}{" "}
                        {managerArea}
                      </>
                    )
                  )}
                </Typography>
              )}
            </Paper>

            <>
              <Typography fontWeight="fontWeightMedium" sx={{ mt: 2 }}>
                {__(`Nadrzędny obszar`)}:
              </Typography>
              <Paper sx={{ p: 3 }}>
                <Typography fontWeight="fontWeightMedium">
                  {areaData.parent?.label ?? areaData.structure?.label}
                </Typography>
              </Paper>
            </>

            <>
              <Box component="span">
                <Typography fontWeight="fontWeightMedium" sx={{ mt: 2 }}>
                  {__(`Wyświetlany dodatkowy obszar`)}:
                </Typography>
                <Typography sx={{ mt: 0.5, fontSize: "0.75rem" }}>
                  {__(
                    `Wyświetlany dodatkowy obszar będzie pokazany na karcie pracownika jako miejsce jego przynależności w
                   strukturze organizacji obok obszaru, do którego należy. Przykład: Zespół kadr, Kadry i płace.`
                  )}
                </Typography>
              </Box>
              <Paper sx={{ p: 3 }}>
                <Typography fontWeight="fontWeightMedium">
                  {areaData.additionalDisplayArea?.label ??
                    __("Brak wyświetlanego dodatkowego obszaru ")}
                </Typography>
              </Paper>
            </>

            <>
              <Typography fontWeight="fontWeightMedium" sx={{ mt: 2 }}>
                {__("Opis")}:
              </Typography>
              <Paper sx={{ p: 3 }}>
                {areaData.description ? (
                  <Typography
                    sx={{
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {areaData.description}
                  </Typography>
                ) : (
                  <Typography fontWeight="fontWeightMedium">
                    {__("Brak opisu")}
                  </Typography>
                )}
              </Paper>
            </>

            <Typography fontWeight="fontWeightMedium" sx={{ mt: 2 }}>
              {__(`Pracownicy w obszarze`)}:
            </Typography>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                justifyContent: "start",
              }}
            >
              {areaData.members?.length ? (
                <Grid container spacing={2} sx={{ width: "100%" }}>
                  {areaData.members.map((employee: IAreaMembers) => (
                    <Grid key={employee.id} size={{ xs: 12, lg: 4 }}>
                      <EmployeeChip
                        employeeName={employee.name}
                        fk={employee.fk}
                        area={employee?.area?.label || null}
                        position={employee?.position?.label || null}
                        initials={employee.initials}
                        avatar={employee.avatar || undefined}
                        disableBorder
                        presenceInformation={employee.presenceInformation}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography fontWeight="fontWeightMedium">
                  {__(`Brak członków obszaru`)}
                </Typography>
              )}
            </Paper>

            <Box
              component="span"
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <Typography
                fontWeight="fontWeightMedium"
                sx={{ mt: 2, textTransform: "uppercase" }}
              >
                {__(`Obszar w strukturze`)}:
              </Typography>

              <Typography
                component={Link}
                target="_blank"
                to={
                  isAhr
                    ? `/ahr/organization-structure/configure-structure/structure-view/areas-organization-structure/node/${areaId}`
                    : // eslint-disable-next-line max-len
                      `/company/edit/${companyId}/organization-structure/configure-structure/structure-view/areas-organization-structure/node/${areaId}`
                }
                fontWeight="fontWeightMedium"
                sx={{
                  mt: 2,
                  color: "#000",
                  textDecoration: "underline",
                }}
              >
                {__(`Przejdź do struktury`)}
              </Typography>
            </Box>
            <Paper sx={{ p: 3 }}>
              <EmployeeHierarchy treeData={[hierarchyData]} areaId={areaId} />
            </Paper>
          </>
        )}
      </Box>
    </>
  );
};

export default PreviewArea;
